import { useCallback, useEffect, useRef, useState } from "react";
import type { Moment, VoiceNote } from "@coaching/types";
import { api, ApiError, loadMoments, type Submission } from "../api";

export type SaveState =
    | { kind: "loading" }
    | { kind: "clean" }
    | { kind: "dirty" }
    | { kind: "saving" }
    | { kind: "saved"; at: number }
    | { kind: "error"; message: string; retryAt: number }
    | { kind: "conflict" }
    | { kind: "locked"; message: string }
    | { kind: "load_error"; message: string };

export interface Draft {
    summary: string;
    summaryVoice: VoiceNote | null;
    moments: Moment[];
    revision: number;
}

const DEBOUNCE_MS = 1500;
const RETRY_STEPS_MS = [3_000, 6_000, 12_000, 20_000];

/**
 * The coach's working copy of a review, autosaved through the server with a
 * revision check. A save against a revision this tab did not produce is
 * refused, which is how a second tab shows "changed elsewhere" instead of
 * overwriting. Nothing is ever dropped silently: an unsaved change stays in
 * memory until it is saved, or until the coach chooses to reload.
 */
export function useDraft(submission: Submission | null, editable: boolean) {
    const id = submission?.id ?? null;
    const [draft, setDraft] = useState<Draft>({ summary: "", summaryVoice: null, moments: [], revision: 0 });
    const [save, setSave] = useState<SaveState>({ kind: "loading" });
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const saveRef = useRef(save);
    saveRef.current = save;
    const timer = useRef<number | null>(null);
    const retries = useRef(0);
    const lastSavedByUs = useRef<number | null>(null);
    // Always the latest snapshot, so a reload after a conflict reads what the other tab saved.
    const submissionRef = useRef(submission);
    submissionRef.current = submission;

    const load = useCallback(async () => {
        const latest = submissionRef.current;
        if (!id || !latest) return;
        setSave({ kind: "loading" });
        try {
            const moments = await loadMoments(id);
            const revision = latest.review.revision;
            lastSavedByUs.current = revision;
            setDraft({ summary: latest.review.summary, summaryVoice: latest.review.summaryVoice ?? null, moments, revision });
            setSave({ kind: "clean" });
        } catch (err) {
            setSave({ kind: "load_error", message: (err as Error)?.message || "Could not load the review." });
        }
    }, [id]);

    useEffect(() => { void load(); }, [load]);

    // Another tab or device saved: the stored revision moved past the one we hold.
    useEffect(() => {
        if (!submission) return;
        const remote = submission.review.revision;
        const s = saveRef.current;
        if (s.kind === "loading" || s.kind === "load_error" || s.kind === "conflict" || s.kind === "saving") return;
        if (remote !== draftRef.current.revision && remote !== lastSavedByUs.current) {
            setSave({ kind: "conflict" });
        }
    }, [submission]);

    const flush = useCallback(async () => {
        if (!id) return;
        const s = saveRef.current;
        if (s.kind === "conflict" || s.kind === "locked" || s.kind === "loading") return;
        const d = draftRef.current;
        setSave({ kind: "saving" });
        try {
            const res = await api.saveDraft(id, { summary: d.summary, summaryVoice: d.summaryVoice, moments: d.moments, baseRevision: d.revision });
            lastSavedByUs.current = res.revision;
            retries.current = 0;
            setDraft((cur) => ({ ...cur, revision: res.revision }));
            // A change made while the request was out must still be saved.
            const changedMeanwhile = draftRef.current.summary !== d.summary || draftRef.current.moments !== d.moments || draftRef.current.summaryVoice !== d.summaryVoice;
            setSave(changedMeanwhile ? { kind: "dirty" } : { kind: "saved", at: Date.now() });
            if (changedMeanwhile) schedule(DEBOUNCE_MS);
        } catch (err) {
            const e = err instanceof ApiError ? err : null;
            if (e?.code === "aborted") { setSave({ kind: "conflict" }); return; }
            if (e?.code === "failed-precondition" || e?.code === "not-found" || e?.code === "permission-denied") {
                setSave({ kind: "locked", message: e.message });
                return;
            }
            if (e?.code === "invalid-argument") {
                setSave({ kind: "locked", message: e.message });
                return;
            }
            const wait = RETRY_STEPS_MS[Math.min(retries.current, RETRY_STEPS_MS.length - 1)];
            retries.current += 1;
            setSave({ kind: "error", message: "Could not save. Retrying.", retryAt: Date.now() + wait });
            schedule(wait);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const schedule = useCallback((ms: number) => {
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => { timer.current = null; void flush(); }, ms);
    }, [flush]);

    const markDirty = useCallback(() => {
        const s = saveRef.current;
        if (s.kind === "conflict" || s.kind === "locked" || s.kind === "loading" || s.kind === "load_error") return;
        if (s.kind !== "saving") setSave({ kind: "dirty" });
        schedule(DEBOUNCE_MS);
    }, [schedule]);

    const setSummary = useCallback((summary: string) => {
        if (!editable) return;
        setDraft((cur) => ({ ...cur, summary }));
        markDirty();
    }, [editable, markDirty]);

    const setSummaryVoice = useCallback((summaryVoice: VoiceNote | null) => {
        if (!editable) return;
        setDraft((cur) => ({ ...cur, summaryVoice }));
        markDirty();
    }, [editable, markDirty]);

    const updateMoments = useCallback((fn: (moments: Moment[]) => Moment[]) => {
        if (!editable) return;
        setDraft((cur) => ({ ...cur, moments: fn(cur.moments) }));
        markDirty();
    }, [editable, markDirty]);

    const saveNow = useCallback(() => {
        if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
        return flush();
    }, [flush]);

    const reload = useCallback(async () => {
        if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
        await load();
    }, [load]);

    useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

    useEffect(() => {
        const warn = (e: BeforeUnloadEvent) => {
            const s = saveRef.current;
            if (s.kind === "dirty" || s.kind === "saving" || s.kind === "error") { e.preventDefault(); e.returnValue = ""; }
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, []);

    return { draft, save, setSummary, setSummaryVoice, updateMoments, saveNow, reload };
}
